import { t } from 'i18next';
import { useMutation } from 'react-query';
import { Input, Radio, Select, Checkbox, Row, Col, Button, Form, Rate, Space } from 'antd';
import { SurveyFeedbackStatus } from '@/constants';
import { queryClient, showNotification } from '@/common/utils';
import { markFeedbackAsRead, submitSurveyForSession } from '@/common/services';
import ReactQuillNoImages from './react-quill-custom';
import styles from './styles.module.scss';

type Props = {
  sessionId: string;
  surveyId: string;
  feedbackStatus: SurveyFeedbackStatus;
  questions: API.SurveyQuestionDto[];
  answers: API.SurveyAnswer[];
  onCancel: VoidFunction;
};

export function FeedbackEditableView(props: Props) {
  const [form] = Form.useForm();
  const { feedbackStatus, sessionId, surveyId, questions, answers, onCancel } = props;
  const isDisabled = feedbackStatus === SurveyFeedbackStatus.Completed;
  const isDraft = feedbackStatus === SurveyFeedbackStatus.Draft;
  const getSubmitParams = (values: Record<string, any>) => {
    const surveyAnswers = Object.keys(values)
      .filter((k) => k !== 'isDraft')
      .map((k: keyof typeof values) => {
        const field = questions.find((q) => q.id === k) as API.SurveyQuestionDto;

        if (['Dropdown'].includes(field.questionType)) {
          return {
            surveyAnswerOptions: [
              {
                surveyQuestionOptionId: values[k],
              },
            ],
            surveyQuestionId: k,
          } as API.SurveyMultipleParams;
        }

        if (['MultipleChoice'].includes(field.questionType)) {
          return {
            surveyAnswerOptions: ((values[k] || []) as string[]).map((v) => {
              return {
                surveyQuestionOptionId: v,
              };
            }),
            surveyQuestionId: k,
          } as API.SurveyMultipleParams;
        }

        return {
          answer: values[k],
          surveyQuestionId: k,
        } as API.SurveySingleParams;
      });

    return {
      bookingCalendarEventId: sessionId,
      surveyId,
      surveyAnswers,
      isDraft: values.isDraft,
    };
  };

  const getFeedbackInitialValues = () => {
    if (!answers?.length) {
      return { isDraft: true };
    }

    const values = { isDraft };

    answers.forEach((s) => {
      if (s.surveyAnswerOptions.length) {
        const typeOfQuestion = questions.find((q) => q.id === s.surveyQuestionId);
        if (typeOfQuestion?.questionType === 'MultipleChoice') {
          // @ts-ignore
          values[s.surveyQuestionId] = s.surveyAnswerOptions.map((s) => s.surveyQuestionOptionId);
        } else {
          // @ts-ignore
          values[s.surveyQuestionId] = s.surveyAnswerOptions[0].surveyQuestionOptionId;
        }
      } else {
        // @ts-ignore
        values[s.surveyQuestionId] = s.answer;
      }
    });

    return values;
  };

  const markAsReadAction = useMutation(
    async (values: Object) => {
      await markFeedbackAsRead(sessionId);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries();
        showNotification({ type: 'success', message: t('message.saveSuccess') });
        onCancel();
      },
    }
  );

  const submitAction = useMutation(
    async (values: Object) => {
      await submitSurveyForSession(sessionId, getSubmitParams(values));
      await markFeedbackAsRead(sessionId);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries();
        showNotification({ type: 'success', message: t('message.saveSuccess') });
        onCancel();
      },
    }
  );

  const initialValues = getFeedbackInitialValues();

  return (
    <div className={styles.advisorViewWrapper}>
      <Form
        layout="vertical"
        form={form}
        name="dynamic_form_complex"
        autoComplete="off"
        initialValues={initialValues}
        onFinish={submitAction.mutate}
      >
        {questions.map((q) => {
          const options = q.questionOptions.map((d) => {
            return {
              value: d.id,
              label: d.optionText,
            };
          });
          const secondaryText = q.secondaryText ? (
            <span className={styles.secondaryText}>{q.secondaryText}</span>
          ) : null;
          let label = (
            <div className={styles.labelWrapper}>
              <span dangerouslySetInnerHTML={{ __html: q.questionText }} />
              {secondaryText}
            </div>
          );

          if (q.isPrivate) {
            label = (
              <div className={styles.labelWrapper}>
                <Space align="center">
                  <span dangerouslySetInnerHTML={{ __html: q.questionText }} />
                  <span style={{ fontSize: 12, fontWeight: 'normal', color: '' }}>
                    ({t('myFeedbacks.privateQuestionLabel')})
                  </span>
                </Space>

                {secondaryText}
              </div>
            );
          }

          if (q.questionType === 'Text') {
            return (
              <Form.Item
                name={q.id}
                label={label}
                key={q.id}
                rules={[
                  {
                    required: q.isMandatory,
                    message: t('validation.required'),
                  },
                ]}
              >
                <Input disabled={isDisabled} />
              </Form.Item>
            );
          }

          if (q.questionType === 'ScaleRating') {
            return (
              <Form.Item
                name={q.id}
                label={label}
                key={q.id}
                rules={[
                  {
                    required: q.isMandatory,
                    message: t('validation.required'),
                  },
                ]}
              >
                <Rate />
              </Form.Item>
            );
          }

          if (q.questionType === 'YesNo') {
            return (
              <Form.Item
                name={q.id}
                label={label}
                key={q.id}
                rules={[
                  {
                    required: q.isMandatory,
                    message: t('validation.required'),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="yes">{t('form.yes')}</Radio>
                  <Radio value="no">{t('form.no')}</Radio>
                </Radio.Group>
              </Form.Item>
            );
          }

          if (q.questionType === 'Dropdown') {
            return (
              <Form.Item
                name={q.id}
                label={label}
                key={q.id}
                rules={[
                  {
                    required: q.isMandatory,
                    message: t('validation.required'),
                  },
                ]}
              >
                <Select disabled={isDisabled} options={options} />
              </Form.Item>
            );
          }

          if (q.questionType === 'MultipleChoice') {
            return (
              <Form.Item
                name={q.id}
                label={label}
                key={q.id}
                rules={[
                  {
                    required: q.isMandatory,
                    message: t('validation.required'),
                  },
                ]}
              >
                <Checkbox.Group disabled={isDisabled} options={options} />
              </Form.Item>
            );
          }

          if (q.questionType === 'RichText') {
            return (
              <Form.Item
                name={q.id}
                label={label}
                key={q.id}
                rules={[
                  {
                    required: q.isMandatory,
                    message: t('validation.required'),
                  },
                ]}
              >
                {/**@ts-ignore */}
                <ReactQuillNoImages readOnly={isDisabled} />
              </Form.Item>
            );
          }

          return <div />;
        })}
        {isDisabled && (
          <>
            <Form.Item shouldUpdate>
              <Row justify="end" align="middle" gutter={12}>
                <div />
                <Button
                  htmlType="button"
                  loading={markAsReadAction.isLoading}
                  onClick={markAsReadAction.mutate}
                >
                  {t('myFeedbacks.markAsRead')}
                </Button>
              </Row>
            </Form.Item>
          </>
        )}
        {!isDisabled && (
          <>
            <Form.Item
              name="isDraft"
              valuePropName="checked"
              className={styles.draftSection}
              hidden
            >
              <Checkbox defaultChecked title="">
                {t('myFeedbacks.draftCheckbox')}
              </Checkbox>
            </Form.Item>
            <Form.Item shouldUpdate>
              {() => (
                <Row justify="end" align="middle" gutter={12}>
                  <Col>
                    <Button onClick={onCancel}>{t('form.cancel')}</Button>
                  </Col>
                  <Col>
                    <Button
                      loading={submitAction.isLoading}
                      onClick={() => {
                        form.setFieldValue('isDraft', true);
                        form.submit();
                      }}
                    >
                      {t('myFeedbacks.saveAsDraft')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      loading={submitAction.isLoading}
                      type="primary"
                      onClick={() => {
                        form.setFieldValue('isDraft', false);
                        form.submit();
                      }}
                    >
                      {t('myFeedbacks.saveAndSubmit')}
                    </Button>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </>
        )}
      </Form>
    </div>
  );
}
